import React, { Component } from 'react';
import { mobileTitleStyle } from './style';
import { launcherIconColor } from './theme-color';
import { LAUNCHER, launcherTransition, launcherGlyph } from './component-contract';

// 启动器的每一个数都在组件契约表 LAUNCHER 里（跨端共享，导出到
// sdk/shared/design-tokens.json 的 components.launcher）。这里一个字面量都不留：
// macOS SDK 的 LaneChatLauncherTokens 画的是同一颗按钮，此前它只能照着本文件
// **抄**一份 hover/press/角标的值 —— 抄写就是漂移的开始。
//
// dash 的"按钮大小"滑块（widget_config.button_size）：服务端 SdkController::
// launcherConfig 按 40–100 钳位后下发，theme-color.js 映射成 theme.buttonSize。
// 这里按同一区间再钳一次 —— 服务端已钳不等于客户端可以假设收到的一定合法
// （缓存的旧配置、宿主直传都可能越界）。
// 图标等比缩放，别写死：否则大按钮上图标显小、小按钮上撑满。
const GLYPH_RATIO = LAUNCHER.glyphPx / LAUNCHER.diameterPx;

// ---------------------------------------------------------------------------
// 【任务 B：开关态过渡 —— 选了哪条路，以及为什么】
//
// 想做的是"气泡形变成叉号"那种同一颗按钮里的图标形变。**当前结构下不成立**：
// widget.js 的渲染分支写着 `(isMobile || alwaysUseFloatingButton ||
// closedStyle === 'button') && !isChatOpen`，面板一打开，这颗按钮整个不渲染，
// 没有一个持续存在的宿主可以形变。
//
// 要让它成立得改渲染条件把按钮留在打开态里，而打开态那个 wrapper 会变成
// `display:flex; flexDirection:column` 并且宽高变成面板尺寸（400×700 / 580×860），
// 按钮留在里面就是 header 上方多出一个 flex 子项。绕开它只能改成绝对定位，
// 而那层 wrapper 的宽高过渡带着一句明确的注释：「宽度绝不能用 transform/left
// 做动画（右边缘会跟着跑出视口）」—— 那是踩过坑写下的。为了一个图标形变去动
// 这块布局，风险和收益不成比例。
//
// 所以取第二条路：**只做关闭态自身的过渡质感**，把"形变"降级为进出场衔接。
//   · 出场：面板打开、按钮卸载 —— 交给面板自己的展开过渡承接。
//   · 入场：面板关闭、按钮重新挂载 —— 既有的挂载弹簧（LAUNCHER.mountScale →
//     1，同时 LAUNCHER.mountOpacity → 1）每次都会重放（componentDidMount →
//     rAF），这本来就是"关闭 → 按钮回来"的那段动效，不需要新代码。
//   · 按下：新增图标层级的反馈 —— duotone 底层 0.2 → 0.34（见
//     LAUNCHER.duotonePressBackOpacity）。只动 fill-opacity，合成器属性、
//     不触发重排，也不碰任何布局。
// ---------------------------------------------------------------------------

function clampLauncherSize(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return LAUNCHER.diameterPx;
    return Math.min(Math.max(Math.round(n), LAUNCHER.minDiameterPx), LAUNCHER.maxDiameterPx);
}

export default class ChatFloatingButton extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hovered: false,
            pressed: false,
            mounted: false,
            imageFailedUrl: null,
        };
        this._lastTap = 0;
    }

    componentDidMount() {
        // Trigger mount animation
        requestAnimationFrame(() => {
            this.setState({ mounted: true });
        });
    }

    handleTouchStart = (e) => {
        this.setState({ pressed: true });
    }

    handleTouchEnd = (e) => {
        this.setState({ pressed: false });
        // 300ms 内去重（iOS 双击误触发防护）；touchAction:'manipulation' 已消除 300ms 延迟，
        // 浏览器自己压制 synthetic click，不调 preventDefault —— touch 事件默认 passive，
        // preventDefault 不生效还会报 "Unable to preventDefault inside passive event listener"。
        const now = Date.now();
        if (now - this._lastTap < 350) return;
        this._lastTap = now;
        if (this.props.onClick) this.props.onClick(e);
    }

    handleTouchCancel = () => {
        this.setState({ pressed: false });
    }

    handleClick = (e) => {
        // 短时间内刚处理过 touch，跳过 click 回调避免重复
        if (Date.now() - this._lastTap < 500) return;
        if (this.props.onClick) this.props.onClick(e);
    }

    render() {
        const { color, unreadCount } = this.props;
        const { hovered, pressed, mounted } = this.state;

        const size = clampLauncherSize(this.props.size);
        const glyphPx = Math.round(size * GLYPH_RATIO);
        // 风格归一在契约里做（脏值/缺省 → 出厂风格），与 clampLauncherSize 同理：
        // 服务端已清洗不等于客户端可以假设收到的一定合法。
        const glyph = launcherGlyph(this.props.iconStyle);
        const fill = color || '#2563eb';
        // 图标色：**商户配的值，没配就是白**（widget_config.launcher_icon_color，
        // 服务端已清洗并始终下发，缺省 '#ffffff'）。
        //
        // 这里曾经是 `pickIconOn(fill, '#ffffff')` —— 按对比度自动挑白或近黑。
        // 无障碍上是对的，但**不可预期**：商户从没碰过这一项，却发现图标在浅色
        // 品牌色上自己变成了黑的；换个品牌色又毫无征兆地跳回白。用户裁定
        // （2026-08-10）：默认白，要别的自己在 dash 配。可预期 + 可控 优先于
        // 自动正确。对比度提示留在 dash 侧（配置时告诉商户，而不是替他改）。
        const glyphColor = launcherIconColor(this.props.iconColor);

        const scale = !mounted ? `scale(${LAUNCHER.mountScale})`
            : pressed ? `scale(${LAUNCHER.pressScale})`
            : hovered ? `scale(${LAUNCHER.hoverScale})`
            : 'scale(1)';

        const buttonStyle = {
            ...mobileTitleStyle,
            width: size,
            height: size,
            background: fill,
            transform: scale,
            // 悬停抬一档。此前这里从不改写 box-shadow，而 transition 里却列着它 ——
            // 那条过渡是死的。裁决 2026-08-09：两端取"抬"（macOS 早已如此），
            // 因为"声明了过渡却没实现"本身就是意图的证据，而留一段永不触发的
            // CSS 只会骗后人。
            boxShadow: `var(${hovered ? LAUNCHER.hoverShadowToken : LAUNCHER.shadowToken})`,
            opacity: mounted ? 1 : LAUNCHER.mountOpacity,
            transition: launcherTransition(['transform', 'box-shadow', 'opacity']),
            position: 'relative',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'manipulation',
        };

        return (
            <div
                role="button"
                tabIndex={0}
                aria-label={this.props.ariaLabel || 'Open chat'}
                style={buttonStyle}
                onClick={this.handleClick}
                onMouseEnter={() => this.setState({ hovered: true })}
                onMouseLeave={() => this.setState({ hovered: false })}
                onTouchStart={this.handleTouchStart}
                onTouchEnd={this.handleTouchEnd}
                onTouchCancel={this.handleTouchCancel}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && this.props.onClick) { e.preventDefault(); this.props.onClick(e); } }}
            >
                {/* A merchant image replaces the glyph. A URL that fails to load falls back to
                    the glyph, so the launcher is never an empty circle. */}
                {this.props.imageUrl && this.state.imageFailedUrl !== this.props.imageUrl ? (
                    <img
                        src={this.props.imageUrl}
                        alt=""
                        draggable={false}
                        onError={() => this.setState({ imageFailedUrl: this.props.imageUrl })}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit', display: 'block' }}
                    />
                ) : (
                /* 图标：两层同一个 fill（svg 上的 glyphColor），差别只在
                    fill-opacity —— 不引入第二种颜色，所以任何主色都成立，也不用
                    为底层重新推导一次对比度。子元素一个颜色字面量都不写，
                    颜色探针的"svg 子元素写死颜色"那条正是为此而设。 */
                <svg style={{ paddingTop: glyph.padTopPx }}
                     fill={glyphColor} height={glyphPx} viewBox={`0 0 ${glyph.grid} ${glyph.grid}`} width={glyphPx}
                     xmlns="http://www.w3.org/2000/svg">
                    {glyph.layers.map((layer) => (
                        <path
                            key={layer.role}
                            data-layer={layer.role}
                            d={layer.d}
                            fillOpacity={layer.role === 'back' && pressed
                                ? LAUNCHER.duotonePressBackOpacity
                                : layer.opacity}
                            style={{ transition: launcherTransition(['fill-opacity']) }}
                        />
                    ))}
                </svg>
                )}

                {/* Unread badge */}
                {unreadCount > 0 && (
                    <div style={{
                        position: 'absolute',
                        top: `${LAUNCHER.badgeOffsetPx}px`,
                        right: `${LAUNCHER.badgeOffsetPx}px`,
                        background: LAUNCHER.badgeBgColor,
                        color: LAUNCHER.badgeTextColor,
                        borderRadius: `var(${LAUNCHER.badgeRadiusToken})`,
                        minWidth: `${LAUNCHER.badgeMinWidthPx}px`,
                        height: `${LAUNCHER.badgeHeightPx}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: `${LAUNCHER.badgeFontSizePx}px`,
                        fontWeight: String(LAUNCHER.badgeFontWeight),
                        padding: `0 ${LAUNCHER.badgePadHPx}px`,
                        boxShadow: `var(${LAUNCHER.badgeShadowToken})`,
                    }}>
                        {unreadCount > LAUNCHER.badgeMaxCount ? `${LAUNCHER.badgeMaxCount}+` : unreadCount}
                    </div>
                )}
            </div>
        );
    }
}
